import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { Link } from '@design-system/Link';
import { InfoIcon } from '@/components/icons/InfoIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@design-system/Dialog';
import { SMPDialogContent } from '../components';
import type { SMPSubscriptionDetail, UIMessagesMap } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { useCheckOrderInProgress, useGetSubscriptionState } from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import { useUIMessages } from '../hooks/useUIMessages';
import { useSMPNavigate } from '../hooks/useSMPNavigate';
import { formatSMPDate, isSubscriptionActiveState, isSubscriptionCancelled, isSubscriptionPaused } from '../utils/smpHelpers';
import { SMP_I_NEED_IT_SOONER_PATH } from '@/constants/paths';

const HOLDING_PATTERN = 'HOLDING_PATTERN';

interface SMPLandingPageNextOrderProps {
  subscription: SMPSubscriptionDetail;
  hasRecommendation: boolean;
  hasOrderInProgress: boolean;
  uiMessages?: UIMessagesMap;
}

export const SMPLandingPageNextOrder = ({
  subscription,
  hasRecommendation,
  hasOrderInProgress,
  uiMessages,
}: SMPLandingPageNextOrderProps) => {
  const { subscription_status, next_order_datetime } = subscription;
  const isPaused = isSubscriptionPaused(subscription_status);
  const { getMessageText } = useUIMessages(uiMessages);
  const { withSubId } = useSMPNavigate();

  const { data: orderInProgressData } = useCheckOrderInProgress(subscription.id, {
    enabled: !!subscription.id && !hasOrderInProgress,
  });
  const isOrderNowInProgress = orderInProgressData?.orderNowInProgress ?? false;

  const { data: holdingPatternData } = useGetSubscriptionState(
    HOLDING_PATTERN,
    subscription.id,
    { enabled: !!subscription.id },
  );
  const isOnHoldingPatternState =
    Array.isArray(holdingPatternData?.openStates) &&
    holdingPatternData.openStates.length > 0;

  if (isSubscriptionCancelled(subscription_status) || hasOrderInProgress) {
    return null;
  }

  if (!isSubscriptionActiveState(subscription_status)) {
    return null;
  }

  const displayDate = isPaused && subscription.resume_date
    ? subscription.resume_date
    : next_order_datetime;

  const infoTooltipDescription = isPaused && subscription.resume_date
    ? 'This is the date when your subscription will resume and the next order will be placed.'
    : 'This is the date when the next order will be placed and your payment method will be charged.';

  const isActiveOrPaused = isSubscriptionActiveState(subscription_status);
  const hasNoOrderInProgress = !hasOrderInProgress && !isOrderNowInProgress;

  const holdingPatternText = (isActiveOrPaused && hasNoOrderInProgress && !hasRecommendation)
    ? 'Your order will be placed shortly after your Results & Recommendations are released.'
    : getMessageText(
      'smp_holding_pattern_state_next_order',
      'headerText',
      undefined,
    );;

  return (
    <Card data-testid="smp-nextOrder-card">
      <CardHeader data-testid="smp-nextOrder-header">
        <CardTitle
          className="typography-h3 text-primary"
          data-testid="smp-nextOrder-title"
        >
          Next Order
        </CardTitle>
      </CardHeader>

      <CardContent data-testid="smp-nextOrder-content">
        <div className="flex flex-col gap-6">
          {/* Holding pattern message (when R&R not released and in holding state) */}
          {isOnHoldingPatternState && (
            <p
              className="typography-body text-secondary"
              data-testid="smp-nextOrder-holdingPattern"
            >
              {holdingPatternText}
            </p>
          )}

          {/* Pending R&R message (no recommendation, no holding pattern) */}
          {!isOnHoldingPatternState && !hasRecommendation && (
            <p
              className="typography-body text-secondary"
              data-testid="smp-nextOrder-pendingRnR"
            >
              {getMessageText(
                'next_order_date_explanation_no_rnr',
                'bodyText',
                undefined,
                'Next order date will be available once your results and recommendations are released.',
              )}
            </p>
          )}

          {/* Next Order Date + "I need it sooner" row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Next Order Date (only when recommendation is available) */}
            {!isOnHoldingPatternState && hasRecommendation && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <span
                    className="typography-body font-medium text-primary whitespace-nowrap"
                    data-testid="smp-nextOrder-dateLabel"
                  >
                    Next Order Date
                  </span>

                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        className="inline-flex items-center justify-center size-5 rounded-full text-tertiary hover:text-secondary transition-colors"
                        data-testid="smp-nextOrder-infoIcon"
                        aria-label="Next order date information"
                      >
                        <InfoIcon className="size-4" />
                      </button>
                    </DialogTrigger>
                    <SMPDialogContent>
                      <DialogHeader className="gap-6 text-start">
                        <DialogTitle className="typography-h3 text-primary">
                          Next Order Date
                        </DialogTitle>
                        <DialogDescription className="typography-body text-secondary">
                          {infoTooltipDescription}
                        </DialogDescription>
                      </DialogHeader>
                    </SMPDialogContent>
                  </Dialog>
                </div>

                <span
                  className="typography-body text-primary whitespace-nowrap"
                  data-testid="smp-nextOrder-dateValue"
                >
                  {formatSMPDate(displayDate)}
                </span>
              </div>
            )}

            {/* "I need it sooner" link — always visible, disabled only when order is in progress */}
            {isOrderNowInProgress ? (
              <span
                className="inline-flex items-center text-sm text-tertiary cursor-not-allowed whitespace-nowrap"
                data-testid="smp-nextOrder-needSoonerLink"
              >
                I need it sooner
                <ChevronRightIcon className="size-5 text-tertiary" />
              </span>
            ) : (
              <Link
                to={withSubId(SMP_I_NEED_IT_SOONER_PATH)}
                variant="flat"
                className="inline-flex items-center text-sm text-success whitespace-nowrap"
                data-testid="smp-nextOrder-needSoonerLink"
              >
                I need it sooner
                <ChevronRightIcon className="size-5 text-success" />
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
